import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { billingApi } from '../services/billingApi';
import type { BillingProviderSummary } from '../types/billing';

export interface MapProviderDialogProps {
  open: boolean;
  practiceId: string;
  providerName: string;
  supervisingProviderName?: string | null;
  onClose: () => void;
  onMapped: () => void;
}

// Backend accepts a 1-64 digit ProviderID; mirror that client-side so we
// don't round-trip an obviously-bad value.
const PROVIDER_ID_PATTERN = /^\d{1,64}$/;
const DEFAULT_ID_HELPER = "Numeric PM ProviderID from Office Ally's provider popup";

/** "LAST, FIRST - Specialty" label, robust to null name parts. */
const providerLabel = (p: BillingProviderSummary): string => {
  const name = [p.lastName, p.firstName].filter(Boolean).join(', ') || '(unnamed)';
  return p.specialty ? `${name} - ${p.specialty}` : name;
};

/**
 * Lets an operator map an unmapped Practice-Mate provider to its Office Ally
 * numeric ProviderID. The operator now picks the provider explicitly from the
 * practice's roster (REV-153) rather than relying on a "LAST, FIRST" parse of
 * the claim's provider name - Elation's "FIRST LAST, DPM" forms used to 422.
 *
 * The supervising (LAST, FIRST) form, when present, remains the crosswalk key
 * the backend resolves against at dispatch, so it is the name we send; the
 * picked provider's id is passed alongside so the server skips the name parse.
 *
 * The parent conditionally mounts this component, so a fresh mount resets local
 * state - no reset effect needed.
 */
const MapProviderDialog: React.FC<MapProviderDialogProps> = ({
  open,
  practiceId,
  providerName,
  supervisingProviderName,
  onClose,
  onMapped,
}) => {
  const [oaProviderId, setOaProviderId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<BillingProviderSummary[]>([]);
  const [selected, setSelected] = useState<BillingProviderSummary | null>(null);

  // The supervising (LAST, FIRST) form is the one that resolves the backend
  // provider_id; fall back to the rendering provider name when absent.
  const hasSupervising =
    !!supervisingProviderName && supervisingProviderName !== providerName;
  const chosenName = supervisingProviderName ?? providerName;

  // Load the practice roster on open and best-guess a pre-selection by parsing
  // the crosswalk-key name as "LAST, FIRST" - only when it matches exactly one.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await billingApi.listPracticeProviders(practiceId);
        if (cancelled) return;
        setProviders(list);

        const [last, first] = chosenName.split(',').map((s) => s.trim().toLowerCase());
        if (last && first) {
          const matches = list.filter(
            (p) =>
              (p.lastName ?? '').trim().toLowerCase() === last &&
              (p.firstName ?? '').trim().toLowerCase() === first,
          );
          if (matches.length === 1) setSelected(matches[0]);
        }
      } catch {
        // A roster load failure leaves the dropdown empty; Save stays disabled
        // until a provider is selected, so there's nothing to POST anyway.
        if (!cancelled) setProviders([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, practiceId, chosenName]);

  const trimmedId = oaProviderId.trim();
  const idValid = PROVIDER_ID_PATTERN.test(trimmedId);
  // Note: chosenName can be '' (parent passes providerName ?? '' and ?? does
  // not catch an empty string), so guard against POSTing an empty name.
  const hasName = chosenName.trim().length > 0;
  const canSave = !submitting && idValid && hasName && selected !== null;
  const idFormatError = trimmedId.length > 0 && !idValid;

  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // The crosswalk row is keyed on chosenName (the supervising "LAST, FIRST" form
      // when present), while selected.id is the operator's explicit pick. When the
      // rendering provider differs from the supervising one these can describe
      // different people, but that's harmless: routing uses the typed oaProviderId,
      // and provider_id is an advisory FK, not the money-routing key.
      await billingApi.mapProvider(practiceId, chosenName, trimmedId, selected?.id);
      onMapped();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSave) handleSave();
  };

  return (
    <Dialog
      open={open}
      // Block backdrop/Escape close while a POST is in flight - otherwise the
      // component unmounts mid-request (setState-after-unmount, and the list
      // never reload()s even if the mapping succeeded).
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Map provider to Office Ally</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <DialogContentText component="div">
            Rendering provider: <strong>{providerName || '-'}</strong>
            {hasSupervising && (
              <div style={{ marginTop: 8 }}>
                The mapping will use the supervising provider{' '}
                <strong>{supervisingProviderName}</strong> in "LAST, FIRST" form.
              </div>
            )}
          </DialogContentText>
          {error !== null && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Autocomplete
            options={providers}
            value={selected}
            onChange={(_e, value) => setSelected(value)}
            getOptionLabel={providerLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disabled={submitting}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Provider"
                helperText="Pick the provider this claim belongs to"
                fullWidth
                autoFocus
              />
            )}
            sx={{ mt: 2 }}
          />
          <TextField
            label="PM ProviderID"
            value={oaProviderId}
            onChange={(e) => setOaProviderId(e.target.value)}
            error={idFormatError}
            helperText={idFormatError ? 'Numbers only' : DEFAULT_ID_HELPER}
            inputProps={{ inputMode: 'numeric' }}
            fullWidth
            disabled={submitting}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!canSave}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MapProviderDialog;
